const completionMarkerPrefix = 'mastery:bank-completed:';

export function bankPartProgress(
  questionIds: string[],
  responses: Record<string, string>,
  submittedResponses: Record<string, string>,
  savedSubmission?: BankPartSubmission | null,
) {
  const answered = questionIds.filter((id) => responses[id]?.trim()).length;
  const submitted =
    Boolean(savedSubmission) ||
    (questionIds.length > 0 &&
      questionIds.every(
        (id) =>
          Object.prototype.hasOwnProperty.call(submittedResponses, id) &&
          submittedResponses[id] === (responses[id] || ''),
      ));
  return {
    state: submitted ? 'completed' : answered > 0 ? 'in-progress' : 'not-started',
    answered: savedSubmission
      ? questionIds.filter((id) => savedSubmission.answers[id]?.trim()).length
      : answered,
    total: questionIds.length,
  } as const;
}

export type BankPartSubmission = {
  at: number;
  answers: Record<string, string>;
  durationSeconds: number;
};

/** Only complete, correctly scoped snapshots count as a submitted Part. */
export function readBankPartSubmission(
  storage: Pick<Storage, 'getItem'>,
  key: string,
  questionIds: string[],
): BankPartSubmission | null {
  if (!questionIds.length) return null;
  try {
    const parsed: unknown = JSON.parse(storage.getItem(key) || 'null');
    if (!parsed || typeof parsed !== 'object') return null;
    const marker = parsed as Record<string, unknown>;
    if (!marker.answers || typeof marker.answers !== 'object' || Array.isArray(marker.answers))
      return null;
    const answers = marker.answers as Record<string, unknown>;
    if (!questionIds.every((id) => typeof answers[id] === 'string')) return null;
    return {
      at: typeof marker.at === 'number' ? marker.at : 0,
      answers: Object.fromEntries(questionIds.map((id) => [id, answers[id] as string])),
      durationSeconds:
        typeof marker.durationSeconds === 'number' && marker.durationSeconds >= 0
          ? marker.durationSeconds
          : 0,
    };
  } catch {
    return null;
  }
}

export function bankPartSelectionSearch(search: string) {
  const current = new URLSearchParams(search);
  const next = new URLSearchParams({ view: 'parts' });
  const test = current.get('test');
  if (test) next.set('test', test);
  if (current.get('mode') === 'exam') next.set('mode', 'exam');
  return `?${next.toString()}`;
}

export function bankCompletionMarkerKey(
  scope: string,
  exam: string,
  book: string,
  section: string,
) {
  return `${completionMarkerPrefix}${scope}:${exam}:${book}:${section}`;
}

/** Remove only submitted markers; answer drafts use a separate storage key. */
export function clearBankCompletionMarkers(scope: string) {
  try {
    const prefix = `${completionMarkerPrefix}${scope}:`;
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith(prefix)) localStorage.removeItem(key);
    }
  } catch {
    // Storage is optional when the browser blocks it.
  }
}
